// src/pages/ApplicationChampionshipSeason/shared/steps/Step2Stages/Step2SeasonStages.tsx
import { FunctionComponent } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SeasonType } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { SeasonFormData } from '../../schemas';
import { WizardStepFooter } from '../../components/WizardStepFooter';
import { LeagueStagePanel } from './LeagueStagePanel';
import { KnockoutStagePanel } from './KnockoutStagePanel';
import { HybridStagePanel } from './HybridStagePanel';

type Step2SeasonStagesProps = {
  onBack: () => void;
  onAdvance: () => void;
  onCancel: () => void;
  /**
   * Quando true, esconde add/delete/drag das listas de stages e desabilita o
   * seeding de defaults. Usado no Edit quando a temporada já tem stages reais.
   * Renomear (`name` / `group_name`) continua permitido.
   */
  isReadOnly?: boolean;
};

const PREFIX = 'pages.application_season.components.season_step2';

export const Step2SeasonStages: FunctionComponent<Step2SeasonStagesProps> = ({
  onBack,
  onAdvance,
  onCancel,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const form = useFormContext<SeasonFormData>();
  const seasonType = form.watch('season_type');

  const handleReview = async () => {
    form.setValue('step', 2);
    const isValid = await form.trigger();
    if (isValid) onAdvance();
  };

  const renderPanel = () => {
    switch (seasonType) {
      case SeasonType.LEAGUE:
        return <LeagueStagePanel />;
      case SeasonType.KNOCKOUT:
        return <KnockoutStagePanel isReadOnly={isReadOnly} />;
      case SeasonType.HYBRID:
        return <HybridStagePanel isReadOnly={isReadOnly} />;
      default:
        return null;
    }
  };

  return (
    <Flex direction="column" gap={6}>
      <Text fontSize="18px" fontWeight="semibold">
        {t(`${PREFIX}.title`)}
      </Text>

      {renderPanel()}

      <WizardStepFooter
        onBack={onBack}
        onCancel={onCancel}
        primaryLabel={t('pages.application_season.components.season_wizard.footer.review')}
        onPrimary={handleReview}
        primaryRightIcon={<ChakraFeatherIcon name="arrow-right" boxSize="16px" />}
      />
    </Flex>
  );
};
